import React from 'react';

// Weekly volume chart (Activities, Graphite 2026-10-27). Presentation only — the caller passes the weekly buckets it
// already builds (oldest → newest, the last one is the current week) and, when the stored plan has one, the current
// week's planned km. Values are truncated, never rounded; a week with no running is a 2px stub with no label.

const BAR_AREA = 150;          // px — the tallest bar (or the planned outline) reaches this height
const LIME = '#C8F542';

// Truncate (never round): 1 decimal under 10 km, an integer from 10 up. The epsilon keeps float noise
// (2.3 * 10 = 22.999…) from dropping a decimal.
export const truncKm = (v, decimals) => {
  const f = 10 ** decimals;
  return Math.floor(Number(v || 0) * f + 1e-9) / f;
};
export const formatWeekKm = (v) => (v < 10 ? truncKm(v, 1).toFixed(1) : String(truncKm(v, 0)));

const dm = (d) => `${d.getDate()}.${d.getMonth() + 1}`;
export function weekRangeLabel(start) {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return '';
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return `${dm(start)}–${dm(end)}`;
}

/** Index of the highest week (null when no week has running). The current week counts too: when it IS the max it
 *  keeps its lime fill and only gains the "שיא" label. */
export function peakIndex(weeks) {
  let best = null;
  weeks.forEach((w, i) => { if (w.km > 0 && (best === null || w.km > weeks[best].km)) best = i; });
  return best;
}

export default function WeeklyVolumeChart({ weeks, plannedKm = null, periodWeeks }) {
  if (!Array.isArray(weeks) || weeks.length === 0) return null;
  const cur = weeks.length - 1;
  const planned = plannedKm > 0 ? plannedKm : null;
  const scale = Math.max(...weeks.map((w) => w.km || 0), planned || 0, 1);
  const peak = peakIndex(weeks);
  const dense = weeks.length > 16;            // 6 months / a year: labels only where they mean something
  // From 12 weeks up a column is too narrow for "X / Y": the current bar shows its actual km and the axis line carries
  // "השבוע · X / Y" instead (no overlap with the neighbouring label).
  const compact = weeks.length > 8;
  const curKm = weeks[cur].km || 0;
  const gap = weeks.length <= 8 ? 8 : weeks.length <= 16 ? 4 : 2;
  const h = (km) => Math.max((km / scale) * BAR_AREA, 4);

  return (
    <div>
    <div className="grid items-end" style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0,1fr))`, columnGap: gap }}
         data-testid="weekly-chart">
      {weeks.map((w, i) => {
        const km = w.km || 0;
        const isCur = i === cur;
        const isPeak = i === peak;
        const plan = isCur ? planned : null;
        const barH = km > 0 ? h(km) : 0;
        const planH = plan ? h(plan) : 0;
        const top = Math.max(barH, planH, 2);
        const showLabel = compact ? (km > 0 && (!dense || isCur || isPeak)) : (km > 0 || plan);
        const label = plan && !compact ? `${formatWeekKm(km)} / ${formatWeekKm(plan)}` : formatWeekKm(km);
        const fill = isCur ? LIME : isPeak ? 'var(--g-muted)' : 'var(--g-bar)';
        const labelStyle = isCur ? { color: LIME, fontWeight: 700 }
          : isPeak ? { color: 'var(--g-text)', fontWeight: 800 } : { color: 'var(--g-muted)', fontWeight: 700 };
        const under = isPeak ? 'שיא' : plan && !compact ? 'מתוכנן' : '';
        const aria = `שבוע ${weekRangeLabel(w.start)}: ${formatWeekKm(km)} ק״מ${plan ? `, מתוכנן ${formatWeekKm(plan)} ק״מ` : ''}`;
        return (
          <div key={w.startDate || i} role="img" aria-label={aria} className="flex flex-col items-stretch min-w-0"
               data-testid="week-bar" data-current={isCur || undefined} data-peak={isPeak || undefined}>
            <div className="relative w-full" style={{ height: BAR_AREA + 20 }}>
              {plan && (
                <div className="absolute inset-x-0 bottom-0 rounded-t-[3px]" data-testid="planned-outline"
                     style={{ height: planH, border: `1px dashed ${LIME}` }} />
              )}
              {km > 0 ? (
                <div className="absolute inset-x-0 bottom-0 rounded-t-[3px]" style={{ height: barH, background: fill }} />
              ) : (
                <div className="absolute inset-x-0 bottom-0" style={{ height: 2, background: 'var(--g-line)' }} />
              )}
              {showLabel && (
                <span className="absolute inset-x-0 text-center whitespace-nowrap tabular-nums" data-testid="week-label"
                      style={{ bottom: top + 4, fontSize: 11, lineHeight: 1, fontFamily: 'var(--g-num)', ...labelStyle }}
                      dir="ltr">
                  {label}
                </span>
              )}
            </div>
            <div className="h-4 mt-1 text-center whitespace-nowrap" style={{ fontSize: 10, lineHeight: '16px',
                 color: under === 'מתוכנן' ? LIME : 'var(--g-sub)' }}>
              {(!dense || isCur || isPeak) ? under : ''}
            </div>
          </div>
        );
      })}
    </div>
    {/* axis: "לפני N שבועות" … "השבוע" (lime), no gridlines */}
    <div className="flex justify-between text-[12px] text-text-muted mt-2">
      <span>לפני {periodWeeks || weeks.length} שבועות</span>
      <span className="font-bold" style={{ color: LIME }} data-testid="axis-current">
        השבוע{compact && planned ? <> · <bdi dir="ltr">{formatWeekKm(curKm)} / {formatWeekKm(planned)}</bdi></> : null}
      </span>
    </div>
    </div>
  );
}
